import {
  AgentComponentMode,
  AgentComponentType,
  type AgentComponent,
  type AgentComponentBody,
} from '@reach/shared-types/agent-components';

export interface ComponentDraft {
  name: string;
  description: string;
  trigger: string;
  instructions: string;
  mode: AgentComponentMode;
  toolDefinitionIds: string[];
  tenantName: string;
  tenantDescription: string;
}

export type ComponentDraftChange = Partial<ComponentDraft>;

const BLANK_DRAFT: ComponentDraft = {
  name: '',
  description: '',
  trigger: '',
  instructions: '',
  mode: AgentComponentMode.ALWAYS_ON,
  toolDefinitionIds: [],
  tenantName: '',
  tenantDescription: '',
};

/** The editable fields of a saved component, or blank ones for a component being created. */
export const draftFrom = (component?: AgentComponent): ComponentDraft => {
  if (!component) {
    return BLANK_DRAFT;
  }
  const { name, description, instructions, mode, tenantName, tenantDescription } = component;
  const componentIsSkill = component.type === AgentComponentType.SKILL;
  const componentIsEscalation = component.type === AgentComponentType.ESCALATION;
  return {
    name,
    description,
    trigger: componentIsSkill ? component.trigger : '',
    instructions,
    mode,
    toolDefinitionIds: componentIsEscalation ? [] : component.toolDefinitionIds,
    tenantName: tenantName ?? '',
    tenantDescription: tenantDescription ?? '',
  };
};

/** The request body that saves a draft as a component of the given type. */
export const bodyFrom = (type: AgentComponentType, draft: ComponentDraft): AgentComponentBody => {
  const { trigger, toolDefinitionIds, ...sharedFields } = draft;
  switch (type) {
    case AgentComponentType.SKILL:
      return { ...sharedFields, type: AgentComponentType.SKILL, trigger, toolDefinitionIds };
    case AgentComponentType.GUIDANCE:
      return { ...sharedFields, type: AgentComponentType.GUIDANCE, toolDefinitionIds };
    case AgentComponentType.ESCALATION:
      return { ...sharedFields, type: AgentComponentType.ESCALATION };
  }
};

const withSortedTools = (draft: ComponentDraft): ComponentDraft => ({
  ...draft,
  toolDefinitionIds: [...draft.toolDefinitionIds].sort(),
});

/** True when two drafts would save the same component. */
export const draftsMatch = (left: ComponentDraft, right: ComponentDraft): boolean =>
  JSON.stringify(withSortedTools(left)) === JSON.stringify(withSortedTools(right));
